// Holiday Club invoicing data: Firestore listeners, the manager's direct edits, and the invoicing callables.
//
// The invoice maths is shared with the Cloud Functions (one source of truth for the pending totals shown here and
// the invoices they issue).

import { collection, doc, onSnapshot, query, setDoc, updateDoc, where } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../firebase-config";
import { stub } from "./holidayClubStub";
import type {
  GroupingProblem,
  HolidayInvoice,
  HolidayRegistration,
  HolidayTerm,
} from "../../../preschool.bee-happy.club_functions/functions/src/holiday/holidayInvoicing";

export {
  formatPounds,
  pendingInvoices,
  countDays,
  type GroupingProblem,
  type HolidayInvoice,
  type HolidayRegistration,
  type HolidayTerm,
  type InvoiceContent,
} from "../../../preschool.bee-happy.club_functions/functions/src/holiday/holidayInvoicing";

type Unsubscribe = () => void;

/** `npm run dev` uses in-memory mock data (holidayClubStub.ts) instead of Firestore and the callables. */
const DEV = import.meta.env.DEV;

export const watchTerms = (onData: (terms: HolidayTerm[]) => void, onError: (error: Error) => void): Unsubscribe =>
  DEV ? stub().watchTerms(onData) : onSnapshot(collection(db, "holidayTerms"), (snapshot) => {
    const terms = snapshot.docs.map((d) => ({ ...(d.data() as Omit<HolidayTerm, "id">), id: d.id }));
    onData(terms.sort((a, b) => b.formCreatedAt.localeCompare(a.formCreatedAt)));
  }, onError);

/** The manager's day-date corrections of every term (holidayTermDates), by term id then row label. */
export const watchTermDates = (
  onData: (dates: { [termId: string]: { [label: string]: string } }) => void,
  onError: (error: Error) => void
): Unsubscribe =>
  DEV ? stub().watchTermDates(onData) : onSnapshot(collection(db, "holidayTermDates"), (snapshot) => {
    onData(Object.fromEntries(snapshot.docs.map((d) => [d.id, ((d.data() as { dates?: { [label: string]: string } }).dates ?? {})])));
  }, onError);

export const saveTermDates = async (termId: string, dates: { [label: string]: string }) => {
  if (DEV) return stub().saveTermDates(termId, dates);
  await setDoc(doc(db, "holidayTermDates", termId), { dates });
};

export { termDayDates } from "../../../preschool.bee-happy.club_functions/functions/src/holiday/holidayForm";

export const watchRegistrations = (
  termId: string,
  onData: (registrations: HolidayRegistration[]) => void,
  onError: (error: Error) => void
): Unsubscribe =>
  DEV ? stub().watchRegistrations(termId, onData) : onSnapshot(query(collection(db, "holidayRegistrations"), where("termId", "==", termId)), (snapshot) => {
    onData(snapshot.docs.map((d) => ({ ...(d.data() as Omit<HolidayRegistration, "id">), id: d.id })));
  }, onError);

export const watchInvoices = (
  termId: string,
  onData: (invoices: HolidayInvoice[]) => void,
  onError: (error: Error) => void
): Unsubscribe =>
  DEV ? stub().watchInvoices(termId, onData) : onSnapshot(query(collection(db, "holidayInvoices"), where("termId", "==", termId)), (snapshot) => {
    onData(snapshot.docs.map((d) => d.data() as HolidayInvoice).sort((a, b) => a.invoiceNo - b.invoiceNo));
  }, onError);

export interface RegistrationAdminChange {
  cancelled?: boolean;
  discountPercentage?: number;
  discountReason?: string;
}

/** Sets manager fields on registrations that are not invoiced yet (siblings get the same values). */
export const updateRegistrationsAdmin = async (registrationIds: string[], change: RegistrationAdminChange) => {
  if (DEV) return stub().updateRegistrationsAdmin(registrationIds, { ...change });
  const fields: Record<string, string | number | boolean> = {};
  for (const [key, value] of Object.entries(change)) {
    if (value !== undefined) fields[`admin.${key}`] = value;
  }
  await Promise.all(registrationIds.map((id) => updateDoc(doc(db, "holidayRegistrations", id), fields)));
};

export const updateInvoice = async (
  invoiceNo: number,
  change: { paid?: boolean; paidDate?: string | null; cancelled?: boolean }
) => {
  if (DEV) return stub().updateInvoice(invoiceNo, change);
  await updateDoc(doc(db, "holidayInvoices", String(invoiceNo)), change);
};

export interface CreateInvoicesResult {
  created: { invoiceNo: number; email: string; totalPence: number; draftId: string | null; error: string | null }[];
  problems: GroupingProblem[];
}

export const createHolidayInvoices = async (termId: string): Promise<CreateInvoicesResult> =>
  DEV ? stub().createHolidayInvoices(termId) : (await httpsCallable<{ termId: string }, CreateInvoicesResult>(functions, "createHolidayInvoices")({ termId })).data;

export const recreateHolidayInvoice = async (invoiceNo: number) =>
  DEV ? stub().recreateHolidayInvoice(invoiceNo) : (await httpsCallable<{ invoiceNo: number }, { invoiceNo: number; draftId: string; totalPence: number }>(
    functions, "recreateHolidayInvoice")({ invoiceNo })).data;

export const createHolidayReceipt = async (invoiceNo: number, paymentDate: string) =>
  DEV ? stub().createHolidayReceipt(invoiceNo, paymentDate) : (await httpsCallable<{ invoiceNo: number; paymentDate: string }, { invoiceNo: number; draftId: string }>(
    functions, "createHolidayReceipt")({ invoiceNo, paymentDate })).data;

/** Today as YYYY-MM-DD in the browser's local time. */
export const today = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
};
